import {
  BaseField,
  buildForm,
  Button,
  FormFieldProps,
  withConditional,
} from "@bleu.builders/ui";
import {
  ArrowRightCircleIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/solid";
import { TrashIcon } from "@radix-ui/react-icons";
import React, { useEffect } from "react";
import { useFieldArray } from "react-hook-form";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/Accordion";

export interface TierArrayFieldProps extends BaseField {
  type: "tier_array";
  defaultValues?: Record<string, unknown>;
  fields: Array<FormFieldProps>;
  length?: {
    maximum?: number;
    minimum?: number;
  };
}

export const TierArrayField = withConditional<TierArrayFieldProps>(
  ({ form, field, customComponents }) => {
    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: field.name,
    });

    useEffect(() => {
      adjustFieldsLengthToMinimum();
    }, [field?.length?.minimum]);

    const adjustFieldsLengthToMinimum = () => {
      if (field?.length?.minimum && fields.length < field.length.minimum) {
        const emptyFields = new Array(
          field.length.minimum - fields.length,
        ).fill(field.defaultValues);
        append(emptyFields, { shouldFocus: false });
      }
    };

    const handleAppend = () => {
      const lastField = fields[fields.length - 1];
      const lastPointsMax = form.getValues(
        `${field.name}.${fields.length - 1}.points_threshold_max`,
      );
      const newPointsMin = lastPointsMax ? lastPointsMax + 1 : 1000;

      append({
        ...field.defaultValues,
        points_threshold_min: newPointsMin,
        points_threshold_max: null,
      });
    };

    const handleRemove = (index: number) => {
      const fieldArray = form.getValues(field.name);
      const fieldToRemove = fieldArray[index];

      if (fieldToRemove.id) {
        const updatedField = { ...fieldToRemove, _destroy: true };
        const newFields = [...fieldArray];
        newFields[index] = updatedField;
        form.setValue(field.name, newFields);
      } else {
        remove(index);
      }
    };

    return (
      <div className="w-full">
        <Accordion type="single" collapsible className="w-full">
          {fields.map((rhfField, index) => {
            const fieldValues = form.getValues(`${field.name}.${index}`);
            if (fieldValues?._destroy) return null;

            return (
              <AccordionItem
                value={`tier-${index}`}
                key={rhfField.id}
                className="border-b"
              >
                <AccordionTrigger className="flex w-full justify-between p-4 hover:no-underline">
                  <div className="flex items-center gap-4 w-full">
                    <span className="text-lg font-semibold">
                      {fieldValues?.name || `Tier ${index + 1}`}
                    </span>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{fieldValues?.points_threshold_min || 0} pts</span>
                      <ArrowRightCircleIcon className="size-4" />
                      <span>
                        {fieldValues?.points_threshold_max
                          ? `${fieldValues.points_threshold_max} pts`
                          : "∞"}
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 p-4">
                    {buildForm(field.fields, form, index, customComponents)}
                    {index !== 0 && index !== 1 && (
                      <Button
                        variant="destructive"
                        size="icon"
                        type="button"
                        onClick={() => handleRemove(index)}
                        className="mt-4"
                      >
                        <TrashIcon className="size-6" />
                      </Button>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <Button
          type="button"
          onClick={handleAppend}
          className="w-full mt-4"
          disabled={
            field?.length?.maximum !== undefined &&
            fields.length >= field.length.maximum
          }
        >
          <PlusCircleIcon className="mr-2 size-4" /> Add New Tier
        </Button>
      </div>
    );
  },
);
